import React from 'react';
import { Box, Typography, Grid, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { BarChart, Layers, Sync } from '@mui/icons-material';

const features = [
  {
    icon: <BarChart sx={{ fontSize: 40, color: '#FFFFFF' }} />,
    title: 'Increase Efficiency',
    description:
      'Automates data processes, reducing manual tasks and saving valuable time.',
  },
  {
    icon: <Layers sx={{ fontSize: 40, color: '#FFFFFF' }} />,
    title: 'Scalability',
    description:
      'Handles growing data volumes, ensuring performance remains optimal as your business expands.',
  },
  {
    icon: <Sync sx={{ fontSize: 40, color: '#FFFFFF' }} />,
    title: 'Data Consistency',
    description:
      'Ensures accurate and reliable data by applying standardization and validation rules during transformation.',
  },
];

const MidSection = () => {
  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 8, md: 12 },
        px: { xs: 2, md: 4 },
        backgroundColor: '#052B7E',
        backgroundImage: 'linear-gradient(135deg, #052B7E 0%, #0056D2 100%)',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.0rem' },
              mb: 2,
              letterSpacing: '-0.02em',
              maxWidth: '800px',
              mx: 'auto',
              color: '#ECECECB2',
            }}
          >
            Maximize Your Data Potential with SkyShift
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: { xs: 8, md: 10 },
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.125rem' },
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.74)',
            }}
          >
            SkyShift automates data extraction, transformation, and
            loading—delivering clean, scalable pipelines for real-time insights
            and smarter decisions.
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 4, md: 6 }} justifyContent="center">
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2 + index * 0.2,
                  ease: 'easeOut',
                }}
              >
                <Box
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: '20px',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(15px) saturate(180%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    textAlign: 'left',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                      backdropFilter: 'blur(20px) saturate(200%)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      mb: 1.5,
                      color: 'white',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: '#ffffff', fontSize: '0.9rem', fontWeight: 400 }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default MidSection;
